import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// UX-S1 — Promise-based in-app replacement for the native browser
// confirm dialog. Matches the existing modal pattern (overlay + centered
// panel). Confirm button focused on open; Escape = cancel; Enter =
// confirm; click on overlay = cancel; the promise resolves exactly once.

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'Please confirm',
  message: '',
  confirmLabel: 'OK',
  cancelLabel: 'Cancel',
  danger: false,
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { ...options, resolve }
  const confirmBtnRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setState({ ...DEFAULTS, ...options, resolve });
    });
  }, []);

  const close = useCallback((value) => {
    setState((cur) => {
      if (cur && cur.resolve) cur.resolve(value);
      return null;
    });
  }, []);

  // Escape / Enter handling while the dialog is open.
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(false); }
      else if (e.key === 'Enter') { e.preventDefault(); close(true); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [state, close]);

  // Auto-focus the confirm button on open so Enter immediately confirms.
  useEffect(() => {
    if (state && confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }, [state]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="ux-confirm-backdrop"
          data-testid="ux-confirm-backdrop"
          onClick={() => close(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div
            className="ux-confirm-dialog"
            data-testid="ux-confirm-dialog"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, minWidth: 360,
              maxWidth: '90vw', boxShadow: '0 10px 40px rgba(15,23,42,0.25)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 data-testid="ux-confirm-title" style={{ margin: 0, fontSize: 16 }}>{state.title}</h3>
            </div>
            <div
              data-testid="ux-confirm-message"
              style={{ padding: '16px 18px', fontSize: 14, whiteSpace: 'pre-wrap', color: '#1f2937' }}
            >
              {state.message}
            </div>
            <div
              style={{
                padding: '12px 18px', borderTop: '1px solid #e5e7eb',
                display: 'flex', justifyContent: 'flex-end', gap: 8,
              }}
            >
              <button
                type="button"
                data-testid="ux-confirm-cancel"
                onClick={() => close(false)}
                style={btnSecondary}
              >
                {state.cancelLabel}
              </button>
              <button
                type="button"
                ref={confirmBtnRef}
                data-testid="ux-confirm-ok"
                onClick={() => close(true)}
                style={state.danger ? btnDanger : btnPrimary}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// useConfirm() returns a function: confirm(options) => Promise<boolean>.
// Returns true on confirm, false on cancel / Escape / overlay click.
// A missing provider is a wiring bug — we throw both in dev and prod so
// it's loud rather than silently auto-confirming the destructive action.
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm() requires a <ConfirmProvider> ancestor.');
  }
  return ctx;
}

const btnPrimary = {
  padding: '6px 14px', background: '#0f172a', color: '#fff',
  border: '1px solid #0f172a', borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
const btnDanger = {
  padding: '6px 14px', background: '#c0392b', color: '#fff',
  border: '1px solid #c0392b', borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
const btnSecondary = {
  padding: '6px 14px', background: '#fff', color: '#1f2937',
  border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
